import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Card from '@mui/material/Card'
import CardContent from '@mui/material/CardContent'
import Container from '@mui/material/Container'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import PlaceIcon from '@mui/icons-material/Place'
import DirectionsIcon from '@mui/icons-material/Directions'
import WhatsAppIcon from '@mui/icons-material/WhatsApp'
import PhoneIcon from '@mui/icons-material/Phone'
import EmailIcon from '@mui/icons-material/Email'
import { useTranslation } from 'react-i18next'

import { PageMeta } from '../../components/common/PageMeta'
import { LocationMap } from '../../components/location/LocationMap'
import { DIRECTIONS_URL, GOOGLE_LISTING_URL } from '../../content/presence'

const PROPERTY = {
  name: 'Lomasaaret Lakeside Cottages',
  address: 'Joensuuntie 3186',
  postal: '58200 Kerimäki',
  country: 'Finland',
  checkInFrom: '15:00',
  checkOutUntil: '12:00',
  // Grouped so the two forms never drift apart: one is what a person reads, the other is
  // what tel: and wa.me require. Written out by hand in two places, they diverge the first
  // time the number changes.
  phoneDisplay: '+358 44 092 2651',
  phoneE164: '+358440922651',
  email: 'lomasaaret@mail.ru',
}

/** wa.me wants the number with no plus and no spaces; anything else opens an empty chat. */
const WHATSAPP = `https://wa.me/${PROPERTY.phoneE164.replace('+', '')}`

export function ContactPage() {
  const { t } = useTranslation()

  return (
    <Container sx={{ py: { xs: 6, md: 10 } }}>
      <PageMeta title={t('contact.title')} path="/contact" />

      <Typography variant="h1" sx={{ mb: 4 }}>
        {t('contact.title')}
      </Typography>

      <Box
        sx={{
          display: 'grid',
          gap: 3,
          gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' },
        }}
      >
        <Card>
          <CardContent sx={{ p: 3 }}>
            <Typography variant="overline" color="primary" sx={{ display: 'block', mb: 1 }}>
              💬 {t('contact.talkToUs')}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {t('contact.talkToUsBody')}
            </Typography>

            <Stack spacing={1.5}>
              <Button
                href={WHATSAPP}
                target="_blank"
                rel="noopener noreferrer"
                startIcon={<WhatsAppIcon />}
                variant="contained"
                sx={{ justifyContent: 'flex-start' }}
              >
                {t('contact.whatsApp')}
              </Button>
              <Button
                // tel: rather than plain text. On a phone it dials; on a laptop it is still
                // selectable, and a guest standing at a wrong gate is on a phone.
                href={`tel:${PROPERTY.phoneE164}`}
                startIcon={<PhoneIcon />}
                variant="outlined"
                sx={{ justifyContent: 'flex-start', direction: 'ltr' }}
              >
                {PROPERTY.phoneDisplay}
              </Button>
              <Button
                href={`mailto:${PROPERTY.email}`}
                startIcon={<EmailIcon />}
                variant="outlined"
                sx={{ justifyContent: 'flex-start', direction: 'ltr' }}
              >
                {PROPERTY.email}
              </Button>
            </Stack>
          </CardContent>
        </Card>

        <Card>
          <CardContent sx={{ p: 3 }}>
            <Typography variant="overline" color="primary" sx={{ display: 'block', mb: 1 }}>
              📍 {t('contact.address')}
            </Typography>
            <Typography variant="h5" sx={{ mb: 0.5 }}>
              {PROPERTY.name}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {PROPERTY.address}
              <br />
              {PROPERTY.postal}
              <br />
              {PROPERTY.country}
            </Typography>
            <Stack direction="row" spacing={1} sx={{ mt: 2, flexWrap: 'wrap', rowGap: 1 }}>
              <Button
                href={GOOGLE_LISTING_URL}
                target="_blank"
                rel="noopener noreferrer"
                startIcon={<PlaceIcon />}
                variant="outlined"
                size="small"
              >
                {t('contact.openInMaps')}
              </Button>
              <Button
                href={DIRECTIONS_URL}
                target="_blank"
                rel="noopener noreferrer"
                startIcon={<DirectionsIcon />}
                variant="contained"
                size="small"
              >
                {t('contact.directions')}
              </Button>
            </Stack>
          </CardContent>
        </Card>

        <Card>
          <CardContent sx={{ p: 3 }}>
            <Typography variant="overline" color="primary" sx={{ display: 'block', mb: 1 }}>
              🕐 {t('contact.checkInOut')}
            </Typography>
            <Stack spacing={1}>
              <Typography variant="body1">
                {t('contact.checkInFrom', { time: PROPERTY.checkInFrom })}
              </Typography>
              <Typography variant="body1">
                {t('contact.checkOutUntil', { time: PROPERTY.checkOutUntil })}
              </Typography>
            </Stack>
          </CardContent>
        </Card>

        <Box sx={{ gridColumn: { md: '1 / -1' } }}>
          <LocationMap />
        </Box>

        <Card sx={{ gridColumn: { md: '1 / -1' } }}>
          <CardContent sx={{ p: 3 }}>
            <Typography variant="overline" color="primary" sx={{ display: 'block', mb: 1.5 }}>
              🚗 {t('contact.gettingHere')}
            </Typography>
            <Stack spacing={1.5}>
              <Stack direction="row" spacing={1.5} alignItems="flex-start">
                <Box aria-hidden sx={{ fontSize: '1.3rem', lineHeight: 1.3 }}>
                  ✈️
                </Box>
                <Typography variant="body2" color="text.secondary">
                  {t('contact.byAir')}
                </Typography>
              </Stack>
              <Stack direction="row" spacing={1.5} alignItems="flex-start">
                <Box aria-hidden sx={{ fontSize: '1.3rem', lineHeight: 1.3 }}>
                  🚙
                </Box>
                <Typography variant="body2" color="text.secondary">
                  {t('contact.byCar')}
                </Typography>
              </Stack>
            </Stack>
          </CardContent>
        </Card>
      </Box>
    </Container>
  )
}
